import React, { useEffect, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Card, CardHeader, CardTitle, CardContent, Badge, Button } from '@agion/ui'
import { Container, AlertTriangle } from 'lucide-react'
import { cn } from '@/core/analytics_utils'
import { queryInstant } from '@/core/observability_api_client'
import type { PodStatus } from '@/core/observability_types'

export const KubernetesPage: React.FC = () => {
  const { t } = useTranslation('pages')
  const [pods, setPods] = useState<PodStatus[]>([])
  const [loading, setLoading] = useState(true)
  const [nsFilter, setNsFilter] = useState<string>('all')

  const fetchPods = useCallback(async () => {
    try {
      // Use OTEL metrics from services to derive pod info
      // kube_pod_info and container metrics from kube-state-metrics (if deployed)
      // Fallback: use up{} targets + process metrics
      const [upRes, cpuRes, memRes] = await Promise.all([
        queryInstant('up{job=~"analytics.*|auth.*|governance.*|orchestration.*|streaming.*|infra.*"}'),
        queryInstant('sum(rate(process_cpu_seconds_total[5m])) by (job, instance, namespace)'),
        queryInstant('sum(process_resident_memory_bytes) by (job, instance, namespace)'),
      ])

      const upResults = upRes.data?.result || []
      const cpuMap = new Map<string, number>()
      const memMap = new Map<string, number>()
      for (const r of (cpuRes.data?.result || [])) {
        const key = r.metric['instance'] || r.metric['job'] || ''
        if (key && r.value) cpuMap.set(key, parseFloat(r.value[1]) || 0)
      }
      for (const r of (memRes.data?.result || [])) {
        const key = r.metric['instance'] || r.metric['job'] || ''
        if (key && r.value) memMap.set(key, parseFloat(r.value[1]) || 0)
      }

      const podList: PodStatus[] = []

      for (const r of upResults) {
        const podName = r.metric['instance'] || r.metric['job'] || 'unknown'
        const name = podName
        const ns = r.metric['namespace'] || ''

        const isUp = r.value ? parseFloat(r.value[1]) === 1 : false
        const cpu = (cpuMap.get(podName) || 0) * 1000
        const mem = memMap.get(podName) || 0

        podList.push({
          name,
          namespace: ns,
          status: isUp ? 'running' : 'failed',
          restarts: 0, // kube-state-metrics needed for this
          cpuUsage: cpu,
          memoryUsage: mem,
        })
      }

      podList.sort((a, b) => {
        if (a.status !== b.status) return a.status === 'failed' ? -1 : 1
        return a.name.localeCompare(b.name)
      })

      setPods(podList)
    } catch {
      // Keep previous state
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchPods()
    const interval = setInterval(fetchPods, 30000)
    return () => clearInterval(interval)
  }, [fetchPods])

  const namespaces = [...new Set(pods.map(p => p.namespace))].filter(Boolean).sort()
  const filteredPods = nsFilter === 'all' ? pods : pods.filter(p => p.namespace === nsFilter)
  const failedCount = pods.filter(p => p.status === 'failed').length

  const fmtMem = (bytes: number) => {
    if (bytes === 0) return '-'
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(0)} MB`
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`
  }

  return (
    <div className="container-standard">
      <div className="page-header">
        <div className="flex items-center gap-3">
          <Container className="h-6 w-6 text-slate-600 dark:text-slate-400" />
          <div>
            <h1 className="page-title">{t('observability.kubernetesTitle', 'Kubernetes')}</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {t('observability.kubernetesDescription', 'Pod status and resource usage across namespaces')}
            </p>
          </div>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <Card>
          <CardContent className="py-4 flex items-center gap-3">
            <div className="rounded-lg p-2.5 bg-blue-500"><Container className="h-5 w-5 text-white" /></div>
            <div>
              <p className="text-sm text-slate-500 dark:text-slate-400">{t('observability.totalPods', 'Total Pods')}</p>
              <p className="text-2xl font-semibold text-slate-900 dark:text-slate-100">{pods.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-4 flex items-center gap-3">
            <div className={cn('rounded-lg p-2.5', failedCount > 0 ? 'bg-red-500' : 'bg-emerald-500')}>
              <AlertTriangle className="h-5 w-5 text-white" />
            </div>
            <div>
              <p className="text-sm text-slate-500 dark:text-slate-400">{t('observability.failedPods', 'Failed')}</p>
              <p className="text-2xl font-semibold text-slate-900 dark:text-slate-100">{failedCount}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-4 flex items-center gap-3">
            <div className="rounded-lg p-2.5 bg-violet-500"><Container className="h-5 w-5 text-white" /></div>
            <div>
              <p className="text-sm text-slate-500 dark:text-slate-400">{t('observability.namespaces', 'Namespaces')}</p>
              <p className="text-2xl font-semibold text-slate-900 dark:text-slate-100">{namespaces.length}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Namespace filter */}
      <div className="flex gap-2 mb-4 flex-wrap">
        <Button
          variant="ghost"
          onClick={() => setNsFilter('all')}
          className={cn(
            'px-3 py-1 rounded-full text-xs font-medium transition-colors h-auto',
            nsFilter === 'all' ? 'bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900' : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400'
          )}
        >
          {t('observability.all', 'All')}
        </Button>
        {namespaces.map(ns => (
          <Button
            key={ns}
            variant="ghost"
            onClick={() => setNsFilter(ns)}
            className={cn(
              'px-3 py-1 rounded-full text-xs font-medium transition-colors h-auto',
              nsFilter === ns ? 'bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900' : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400'
            )}
          >
            {ns}
          </Button>
        ))}
      </div>

      {/* Pod table */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">{t('observability.pods', 'Pods')}</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            {/* eslint-disable-next-line no-restricted-syntax */}
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-700">
                  <th className="text-left px-4 py-3 text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">{t('observability.pod', 'Pod')}</th>
                  <th className="text-left px-4 py-3 text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">{t('observability.namespace', 'Namespace')}</th>
                  <th className="text-left px-4 py-3 text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">{t('observability.status', 'Status')}</th>
                  <th className="text-right px-4 py-3 text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">CPU</th>
                  <th className="text-right px-4 py-3 text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">{t('observability.memory', 'Memory')}</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  [...Array(6)].map((_, i) => (
                    <tr key={i} className="border-b border-slate-100 dark:border-slate-700/50">
                      <td colSpan={5} className="px-4 py-3">
                        <div className="h-5 animate-pulse bg-slate-100 dark:bg-slate-700 rounded" />
                      </td>
                    </tr>
                  ))
                ) : filteredPods.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-12 text-center text-sm text-slate-400">
                      {t('observability.noPods', 'No pods found.')}
                    </td>
                  </tr>
                ) : (
                  filteredPods.map(pod => (
                    <tr key={`${pod.namespace}/${pod.name}`} className="border-b border-slate-100 dark:border-slate-700/50">
                      <td className="px-4 py-3 text-sm font-medium text-slate-900 dark:text-slate-100">{pod.name}</td>
                      <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400">{pod.namespace}</td>
                      <td className="px-4 py-3">
                        <Badge variant={pod.status === 'running' ? 'default' : 'destructive'}>{pod.status}</Badge>
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-slate-600 dark:text-slate-400">
                        {/* eslint-disable-next-line i18next/no-literal-string */}
                        {pod.cpuUsage > 0 ? `${pod.cpuUsage.toFixed(0)}m` : '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-slate-600 dark:text-slate-400">
                        {fmtMem(pod.memoryUsage)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
